/**
 * AdminDashboard — system-wide metrics for super_admin and sales_admin roles.
 *
 * 8 metric cards in a responsive grid (3 columns on lg, 2 on md, 1 on mobile).
 * Overdue invoices card highlights red when count > 0.
 */

import type { AdminDashboardMetrics } from '@/hooks/useDashboard'
import { formatCents } from './formatters'

interface MetricCardProps {
  label: string
  value: string | number
  subLabel?: string
  variant?: 'default' | 'warning' | 'success'
}

function MetricCard({ label, value, subLabel, variant = 'default' }: MetricCardProps) {
  const borderColor =
    variant === 'warning'
      ? 'border-red-200 bg-red-50'
      : variant === 'success'
        ? 'border-green-200 bg-green-50'
        : 'border-gray-200 bg-white'

  const valueColor =
    variant === 'warning'
      ? 'text-red-900'
      : variant === 'success'
        ? 'text-green-900'
        : 'text-gray-900'

  const labelColor =
    variant === 'warning'
      ? 'text-red-700'
      : variant === 'success'
        ? 'text-green-700'
        : 'text-gray-500'

  return (
    <div className={`rounded-lg border p-6 shadow-sm ${borderColor}`}>
      <p className={`text-sm font-medium ${labelColor}`}>{label}</p>
      <p className={`mt-2 text-3xl font-bold ${valueColor}`}>{value}</p>
      {subLabel && <p className={`mt-1 text-xs ${labelColor}`}>{subLabel}</p>}
    </div>
  )
}

interface AdminDashboardProps {
  metrics: AdminDashboardMetrics
}

export function AdminDashboard({ metrics }: AdminDashboardProps) {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard del Sistema</h1>
        <p className="mt-1 text-sm text-gray-500">
          Metricas agregadas del sistema — actualizacion cada 5 minutos
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricCard
          label="Clientes Activos"
          value={metrics.totalClients.toLocaleString('es-CO')}
          subLabel="clientes no eliminados"
        />

        <MetricCard
          label="Cuentas Activas"
          value={metrics.activeAccounts.toLocaleString('es-CO')}
          subLabel={`de ${metrics.totalAccounts.toLocaleString('es-CO')} cuentas totales`}
          variant={metrics.activeAccounts > 0 ? 'success' : 'default'}
        />

        <MetricCard
          label="MRR"
          value={formatCents(metrics.mrrCents)}
          subLabel="ingreso mensual recurrente"
          variant={metrics.mrrCents > 0 ? 'success' : 'default'}
        />

        <MetricCard
          label="Facturas Vencidas"
          value={metrics.overdueInvoices.toLocaleString('es-CO')}
          subLabel={metrics.overdueInvoices > 0 ? 'requieren atencion' : 'sin facturas vencidas'}
          variant={metrics.overdueInvoices > 0 ? 'warning' : 'default'}
        />

        <MetricCard
          label="Cotizaciones"
          value={metrics.totalQuotations.toLocaleString('es-CO')}
          subLabel="total en el sistema"
        />

        <MetricCard
          label="Comisiones Pendientes"
          value={formatCents(metrics.pendingCommissionsCents)}
          subLabel="pendientes de aprobacion"
          variant={metrics.pendingCommissionsCents > 0 ? 'warning' : 'default'}
        />

        <MetricCard
          label="Resellers"
          value={metrics.totalResellers.toLocaleString('es-CO')}
          subLabel="socios revendedores activos"
        />

        <MetricCard
          label="Freelancers"
          value={metrics.totalFreelancers.toLocaleString('es-CO')}
          subLabel="socios freelance activos"
        />
      </div>
    </div>
  )
}
